import { ArrowLeftRight, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useExchangeRates } from '@/lib/hooks/use-exchange-rates';
import {
  convertPrice,
  getCurrencySymbol,
  getCurrencyLabel,
  formatPriceWithSymbol,
  type CurrencyType,
} from '@/lib/utils/price.utils';
import { cn } from '@/lib/utils';

export interface PriceConverterProps {
  /** The price amount to convert */
  price: number;
  /** The source currency of the price */
  sourceCurrency: CurrencyType;
  /** Whether to show the component in compact mode */
  compact?: boolean;
  /** Custom className for the container */
  className?: string;
}

export function PriceConverter({
  price,
  sourceCurrency,
  compact = false,
  className,
}: PriceConverterProps) {
  const { t } = useTranslation();
  const { exchangeRates, isLoading } = useExchangeRates();

  // Don't render if price is 0 or no config available
  if (!price || price <= 0 || isLoading) {
    return null;
  }

  // Get all currencies except the source
  const currencies: CurrencyType[] = ['comoros_currency', 'european_currency', 'american_currency'];
  const otherCurrencies = currencies
    .filter(currency => currency !== sourceCurrency)
    .map(currency => ({
      type: currency,
      value: convertPrice(price, sourceCurrency, currency, exchangeRates),
      symbol: getCurrencySymbol(currency),
      label: getCurrencyLabel(currency),
    }));

  if (compact) {
    return (
      <div className={cn('bg-primary/5 border border-primary/20 rounded-lg p-3', className)}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <ArrowLeftRight className="size-3.5 text-primary/70" />
          <span className="text-xs font-medium text-primary/80">
            {t('products.priceEquivalents', { defaultValue: 'Prix équivalents' })}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          {otherCurrencies.map((currency, index) => (
            <span key={currency.type} className="text-primary font-medium">
              {formatPriceWithSymbol(currency.value, currency.type)}
              {index < otherCurrencies.length - 1 && (
                <span className="text-primary/50 ml-2">|</span>
              )}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-muted/50 border rounded-lg p-4', className)}>
      <div className="flex items-center gap-2 mb-1">
        <ArrowLeftRight className="size-5 text-primary" />
        <span className="text-base font-semibold">
          {t('products.priceConversion', { defaultValue: 'Conversion de prix' })}
        </span>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {t('products.priceConversionHelp', {
          defaultValue: 'Votre prix produit dans les autres devises pour informer vos clients potentiels',
        })}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {otherCurrencies.map(currency => (
          <div
            key={currency.type}
            className="bg-background border rounded-lg p-3 text-center"
          >
            <div className="flex flex-col items-center gap-1 mb-2">
              <span className="text-lg font-bold text-primary">
                {currency.symbol}
              </span>
              <span className="text-xs text-muted-foreground">
                {currency.label}
              </span>
            </div>
            <span className="text-lg font-semibold">
              {formatPriceWithSymbol(currency.value, currency.type)}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="size-4 shrink-0 mt-0.5" />
        <span>
          {t('products.priceConversionDisclaimer', {
            defaultValue: 'Les taux de change sont mis à jour périodiquement. Les taux réels peuvent varier.',
          })}
        </span>
      </div>
    </div>
  );
}
